/**
 * 站点上下文 - 根据用户身份动态获取站点ID
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMySites, setCurrentSiteId, type MySiteInfo } from '@/utils/api';
import { isMainDomain } from '@/config/domain';

interface SiteContextType {
  siteId: string;
  siteName: string;
  loading: boolean;
  error: string | null;
  mySites: MySiteInfo[];
  switchSite: (siteId: string) => void;
  setSiteName: (name: string) => void;
  /** 当前是否在子域名（非主域名），子域名下站点锁定不可切换 */
  isSubdomain: boolean;
  /** 子域名锁定的站点ID（仅在 isSubdomain=true 时有值） */
  lockedSiteId: string;
}

const SiteContext = createContext<SiteContextType>({
  siteId: '',
  siteName: '',
  loading: true,
  error: null,
  mySites: [],
  switchSite: () => {},
  setSiteName: () => {},
  isSubdomain: false,
  lockedSiteId: '',
});

/**
 * 获取当前域名
 */
function getCurrentDomain(): string {
  if (typeof window === 'undefined') return '';
  return window.location.host;
}

/**
 * 根据域名获取站点ID（公开接口，不需要登录）
 */
async function fetchSiteIdByDomain(): Promise<{ id: string; name: string } | null> {
  const domain = getCurrentDomain();
  if (!domain) return null;
  
  console.log('🌐 通过域名获取站点ID, domain:', domain);
  
  try {
    const response = await fetch(`/api/sites/by-domain?domain=${encodeURIComponent(domain)}`);
    
    // 打印响应状态以便调试
    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      console.warn('⚠️ by-domain 请求失败:', response.status, response.statusText, errorText.slice(0, 200));
      return null;
    }
    
    // 检查响应是否为 JSON
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      return null;
    }
    
    const data = await response.json();
    
    if (data.code === 0 && data.data) {
      console.log('✅ 域名匹配成功:', data.data.id, data.data.name);
      return {
        id: data.data.id,
        name: data.data.name,
      };
    } else {
      console.warn('⚠️ 域名未匹配到站点:', data.message || '未知原因');
    }
  } catch (e) {
    console.warn('⚠️ 通过域名获取站点信息失败:', e);
  }
  
  return null;
}

export function SiteProvider({ children }: { children: React.ReactNode }) {
  const [siteId, setSiteId] = useState<string>(() => localStorage.getItem('current_site_id') || '');
  const [siteName, setSiteName] = useState<string>(''); // 不使用 localStorage 默认值，由 SiteConfigContext 从后台设置同步
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mySites, setMySites] = useState<MySiteInfo[]>([]);
  // 子域名锁定：在主域名(false)下可以自由切换，在子域名(true)下锁定到域名匹配的站点
  const subdomain = !isMainDomain();
  const [lockedSiteId, setLockedSiteId] = useState<string>('');

  // 切换站点
  const switchSite = useCallback((newSiteId: string) => {
    // 子域名下不允许切换站点
    if (subdomain) {
      console.warn('⚠️ 子域名下不允许切换站点，当前锁定站点:', lockedSiteId);
      return;
    }
    const site = mySites.find(s => s.id === newSiteId);
    setSiteId(newSiteId);
    setCurrentSiteId(newSiteId);
    if (site) {
      setSiteName(site.name);
      localStorage.setItem('current_site_name', site.name);
    }
  }, [mySites, subdomain, lockedSiteId]);

  useEffect(() => {
    async function initSite() {
      setLoading(true);
      
      let finalSiteId = localStorage.getItem('current_site_id') || '';
      let finalSiteName = '';  // 不从 localStorage 读取，由 SiteConfigContext 同步
      
      try {
        // 子域名下：先通过域名获取站点ID（用于锁定校验）
        if (subdomain) {
          console.log('🔒 检测到子域名，将通过域名锁定站点...');
          const siteByDomain = await fetchSiteIdByDomain();
          if (siteByDomain) {
            setLockedSiteId(siteByDomain.id);
            finalSiteId = siteByDomain.id;
            finalSiteName = siteByDomain.name;
            setCurrentSiteId(finalSiteId);
            console.log('✅ 子域名锁定站点:', finalSiteId, finalSiteName);
            
            // 子域名下强制使用域名匹配的站点，不需要检查用户站点列表
            // 如果用户对此站点没有管理权限，RequireAdmin 中间件会拦截
          } else {
            console.warn('⚠️ 子域名未能匹配到站点，回退到普通模式');
          }
        }
        
        const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
        
        if (token) {
          console.log('🔑 已登录，尝试获取用户站点列表...');
          try {
            const res = await getMySites();
            const sites = res.list || [];
            setMySites(sites);
            console.log('📋 用户可管理站点:', sites.length, '个');
            
            if (sites.length > 0) {
              // 子域名且已通过域名匹配到站点 → 强制使用域名站点
              if (subdomain && lockedSiteId) {
                // 检查用户对该站点是否有权限（仅用于日志）
                const hasLockedAccess = sites.some(s => s.id === lockedSiteId);
                console.log(hasLockedAccess ? '✅ 用户对锁定站点有管理权限' : '⚠️ 用户对锁定站点无管理权限（后端会拦截）');
                // 不改变 finalSiteId，继续使用域名锁定的
              } else if (!finalSiteId) {
                // 主域名 + 无缓存 → 自动选第一个 owner 站点
                const preferredSite = sites.find(s => s.role === 'owner') || sites[0];
                finalSiteId = preferredSite.id;
                finalSiteName = preferredSite.name;
                setCurrentSiteId(finalSiteId);
                console.log('🎯 自动选择站点:', finalSiteId, finalSiteName);
              } else {
                // 主域名 + 有缓存 → 验证权限
                const hasAccess = sites.some(s => s.id === finalSiteId);
                if (!hasAccess) {
                  console.warn('⚠️ 缓存的站点无权限，重新选择');
                  const preferredSite = sites.find(s => s.role === 'owner') || sites[0];
                  finalSiteId = preferredSite.id;
                  finalSiteName = preferredSite.name;
                  setCurrentSiteId(finalSiteId);
                } else {
                  const currentSite = sites.find(s => s.id === finalSiteId);
                  if (currentSite) {
                    finalSiteName = currentSite.name;
                    localStorage.setItem('current_site_name', finalSiteName);
                  }
                  console.log('✅ 使用已有 siteId:', finalSiteId);
                }
              }
            } else {
              console.warn('⚠️ 已登录但没有可管理的站点');
            }
          } catch (e) {
            console.warn('⚠️ 获取用户站点列表失败:', e);
          }
        } else {
          console.log('🔓 未登录状态');
        }
        
        // 如果还没有 siteId，尝试通过域名获取（主域名下的兜底）
        if (!finalSiteId && !subdomain) {
          console.log('🌐 尝试通过域名获取站点...');
          const siteByDomain = await fetchSiteIdByDomain();
          if (siteByDomain) {
            finalSiteId = siteByDomain.id;
            finalSiteName = siteByDomain.name;
            setCurrentSiteId(finalSiteId);
            console.log('✅ 域名匹配成功:', finalSiteId);
          } else {
            console.warn('⚠️ 域名匹配失败');
          }
        }
        
        // 终极兜底：直接从后端获取第一个活跃站点（仅在主域名下才兜底，子域名下不兜底）
        if (!finalSiteId && !subdomain) {
          console.log('🔄 终极兜底：尝试获取默认站点...');
          try {
            const response = await fetch('/api/sites/default');
            const data = await response.json();
            if (data.code === 0 && data.data) {
              finalSiteId = data.data.id;
              finalSiteName = data.data.name || data.data.site_name || '';
              setCurrentSiteId(finalSiteId);
              console.log('✅ 默认站点获取成功:', finalSiteId);
            } else {
              console.error('❌ 所有站点解析方式均失败');
            }
          } catch (e) {
            console.error('❌ 默认站点获取异常:', e);
          }
        }
        
        if (finalSiteId) {
          setSiteId(finalSiteId);
          setSiteName(finalSiteName);
          console.log('🏁 最终 siteId:', finalSiteId, '名称:', finalSiteName);
        } else {
          setError('无法确定当前站点');
        }
      } catch (e) {
        setError('初始化站点失败');
        console.error('初始化站点失败:', e);
      } finally {
        setLoading(false);
      }
    }
    
    initSite();
  }, []);

  return (
    <SiteContext.Provider value={{ siteId, siteName, loading, error, mySites, switchSite, setSiteName, isSubdomain: subdomain, lockedSiteId }}>
      {children}
    </SiteContext.Provider>
  );
}

export function useSite() {
  return useContext(SiteContext);
}

export default SiteContext;
